import { Injectable } from '@angular/core';
import {
  Amenity,
  Boondock,
  IBoondock,
  IBoondockWhere,
  IBoondockPoint,
  ISubPhoto,
  Policy,
  SignalReception,
} from '@curbnturf/entities';
import { select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { skipWhile, take } from 'rxjs/operators';
import * as BoondockActions from './boondock.actions';
import { BoondockState } from './boondock.reducer';
import * as BoondockSelectors from './boondock.selectors';

@Injectable({ providedIn: 'root' })
export class BoondockFacade {
  boondocks$: Observable<Boondock[]>;
  entities$: Observable<{ [id: number]: Boondock }>;
  boondocksReadyToEnable$: Observable<Boondock[]>;
  currentUserBoondocks$: Observable<Boondock[]> = of([]);
  selected$: Observable<Boondock | null>;
  selectedOnce$: Observable<Boondock | null>;
  error$: Observable<string | undefined>;
  loaded$: Observable<boolean>;

  constructor(private store: Store<BoondockState>) {
    this.boondocks$ = this.store.pipe(select(BoondockSelectors.getBoondocks));
    this.entities$ = this.store.pipe(select(BoondockSelectors.getBoondockEntities));
    this.boondocksReadyToEnable$ = this.store.pipe(select(BoondockSelectors.getBoondocksReadyToEnable));
    this.currentUserBoondocks$ = this.store.pipe(select(BoondockSelectors.getCurrentUserBoondocks));
    this.selected$ = this.store.pipe(select(BoondockSelectors.getSelectedBoondock));
    this.selectedOnce$ = this.selected$.pipe(
      skipWhile((boondock) => !boondock || !boondock.id),
      take(1),
    );
    this.error$ = this.store.pipe(select(BoondockSelectors.getError));
    this.loaded$ = this.store.pipe(select(BoondockSelectors.getLoaded));
  }

  create(boondock?: IBoondock) {
    this.store.dispatch(BoondockActions.createBoondock({ boondock: new Boondock(boondock) }));
  }

  createAmenity(boondock: IBoondock) {
    this.store.dispatch(BoondockActions.createAmenity({ boondock }));
  }

  createPolicy(boondock: IBoondock) {
    this.store.dispatch(BoondockActions.createPolicy({ boondock }));
  }

  createSignalReception(boondock: IBoondock) {
    this.store.dispatch(BoondockActions.createSignalReception({ boondock }));
  }

  remove(id: number | string) {
    this.store.dispatch(BoondockActions.removeBoondock({ boondockId: id }));
  }

  restore(id: number) {
    this.store.dispatch(BoondockActions.restoreBoondock({ boondockId: id }));
  }

  removeAmenity(boondock: IBoondock, amenity: Amenity) {
    this.store.dispatch(BoondockActions.removeAmenity({ boondock, amenity }));
  }

  removePolicy(boondock: IBoondock, policy: Policy) {
    this.store.dispatch(BoondockActions.removePolicy({ boondock, policy }));
  }

  removeSignalReception(boondock: IBoondock, signalReception: SignalReception) {
    this.store.dispatch(BoondockActions.removeSignalReception({ boondock, signalReception }));
  }

  update(boondock: IBoondock) {
    this.store.dispatch(BoondockActions.updateBoondock({ boondock }));
  }

  select(boondockId?: number) {
    this.store.dispatch(BoondockActions.selectBoondock({ boondockId }));
  }

  selectBoondock(boondock: IBoondock) {
    this.store.dispatch(BoondockActions.selectBoondockByBoondock({ boondock }));
  }

  loadRatings(boondockId: number) {
    this.store.dispatch(BoondockActions.loadBoondockRatings({ boondockId }));
  }

  removePhoto(boondockId: number, index: number): void {
    this.store.dispatch(BoondockActions.removeImageFromBoondock({ boondockId, index }));
  }

  enable(boondock: IBoondock) {
    this.store.dispatch(BoondockActions.enableBoondock({ boondock }));
  }

  disable(boondock: IBoondock) {
    this.store.dispatch(BoondockActions.disableBoondock({ boondock }));
  }

  loaded(boondock: IBoondock) {
    this.store.dispatch(BoondockActions.boondockLoaded({ boondock }));
  }

  loadUserBoondocks(userId?: number) {
    if (userId) {
      this.store.dispatch(BoondockActions.loadBoondocks({ payload: { userId } }));
    } else {
      this.store.dispatch(BoondockActions.loadBoondocks({}));
    }
  }

  loadAllBoondocks(where?: IBoondockWhere) {
    this.store.dispatch(BoondockActions.loadBoondocks({ payload: where }));
  }

  load(boondockId: number) {
    this.store.dispatch(BoondockActions.loadBoondock({ boondockId }));
  }

  clear() {
    this.store.dispatch(BoondockActions.clearBoondocks());
  }

  favorite(point: IBoondockPoint) {
    this.store.dispatch(BoondockActions.favoriteBoondock({ point }));
  }

  unfavorite(point: IBoondockPoint) {
    this.store.dispatch(BoondockActions.unfavoriteBoondock({ point }));
  }

  recropImage(boondockToSave: IBoondock, photo: ISubPhoto) {
    this.store.dispatch(BoondockActions.recropImage({ boondockToSave, photo }));
  }
}
